import React, { Profiler, ProfilerOnRenderCallback, useEffect } from 'react'
import * as ReactDOM from 'react-dom/client'
import {
  createBrowserRouter,
  createRoutesFromElements,
  isRouteErrorResponse,
  Route,
  RouterProvider,
  useRouteError,
} from 'react-router-dom'
import '@css/main.scss'
import { Button } from 'react-bootstrap'
import { App, set_promoted_app_version } from '~/app'
import { LocalStorageKey } from '~/types'
import { ApolloProvider } from '@apollo/client'
import { AuthProvider } from '~/auth/Auth'
import { WindowProvider } from '~/common/hooks/useWindowSize'
import { startup } from '~/api/ServerApi'
import { clientFactory, PreloadAuth } from '~/apolloClient'
import { APIProvider } from '@vis.gl/react-google-maps'
import { ShortcutContextProvider } from '~/contexts/ShortcutContext'
import { PageVisibilityProvider } from '~/contexts/PageVisibilityContext'

const apiKey = JSON.parse(document.getElementById('data/GOOGLE_MAPS_KEY')?.textContent ?? '""')
const libraries = ['places']

// Nothing anywhere in the route tree caught render errors before this, so any transient one
// (most commonly a stale lazy-chunk fetch after a Vite dev HMR reload) tore down and blanked the
// entire app. Dynamic-import failures are the routine Vite-dev case: reload once to recover.
const RouteError = () => {
  const error = useRouteError()
  const message = isRouteErrorResponse(error)
    ? error.statusText
    : error instanceof Error
      ? error.message
      : String(error)
  const isChunkLoadError = /dynamically imported module|module script failed/i.test(message)

  useEffect(() => {
    if (isChunkLoadError && !sessionStorage.getItem(LocalStorageKey.chunkReload)) {
      sessionStorage.setItem(LocalStorageKey.chunkReload, '1')
      globalThis.location.reload()
    }
  }, [isChunkLoadError])

  if (isChunkLoadError) return null

  return (
    <div className="d-flex flex-column align-items-center justify-content-center gap-3" style={{ height: '100vh' }}>
      <h2>Something went wrong</h2>
      <p className="text-muted">{message}</p>
      <Button onClick={() => globalThis.location.reload()}>Reload</Button>
    </div>
  )
}

// Callback function to receive profiling data
const onRenderCallback: ProfilerOnRenderCallback = (): void => {}
const apolloClient = clientFactory(false, set_promoted_app_version)
const container = document.getElementById('react-main')
if (container) {
  void startup()
  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route
        path={'*'}
        errorElement={<RouteError />}
        element={
          <ApolloProvider client={apolloClient}>
            <Profiler id="AppRoot" onRender={onRenderCallback}>
              <PreloadAuth>
                <AuthProvider>
                  <WindowProvider>
                    <ShortcutContextProvider>
                      <PageVisibilityProvider>
                        <APIProvider apiKey={apiKey} libraries={libraries}>
                          <App />
                        </APIProvider>
                      </PageVisibilityProvider>
                    </ShortcutContextProvider>
                  </WindowProvider>
                </AuthProvider>
              </PreloadAuth>
            </Profiler>
          </ApolloProvider>
        }
      />
    )
  )

  ReactDOM.createRoot(container).render(<RouterProvider router={router} />)
}
